import { observer } from 'mobx-react-lite'
import DetailsSection from '../common/DetailsSection'
import DetailsValue from '../common/DetailsValue'
import DischargeInstructionsList from 'components/common/DischargeInstructionsList'
import { formatNoteDescriptionFromBackend } from 'util/notes'
import Modals from 'singletons/Modals'
import ImagesContent from 'components/Dashboard/Main/Visit/TabPanel/tabs/common/ImagesContent'
import { DIRTY_LABELS } from 'constants/notes'
import capitalize from 'lodash/capitalize'
import DefaultNotesBlock from 'components/Dashboard/cards/Visit/common/CollapsedBlock/DefaultNotesBlock'
import DefaultPluralNotesBlock from 'components/Dashboard/cards/Visit/common/CollapsedBlock/DefaultPluralNotesBlock'

type SummarySectionProps = {
  // TODO: improve visit type
  visit: any
  parentVisit?: any
}

const SummarySection = ({ visit, parentVisit }: SummarySectionProps) => {
  const {
    isSuccess,
    followupInstructions,
    dischargeInstructions,
    restriction,
    notes,
    assessmentCompleted,
    images,
    safetyPlan,
    chat,
    canEditPsychotherapyNotes,
    isMemberLocked,
  } = visit || {}

  const privateNotesLabel = `${capitalize(DIRTY_LABELS.psychotherapy)} Notes`

  const displayDischargeInstructions = !!dischargeInstructions?.list?.length && (
    <div className="flex flex-col gap-1">
      <DischargeInstructionsList visit={visit} isSuccess={isSuccess} />
    </div>
  )

  const displayRestrictions = !!restriction?.list?.length && (
    <div className="flex flex-col gap-1">
      {restriction.list.map((r, i) => (
        <p key={`${r}_${i}`}>-&nbsp;{r}</p>
      ))}
    </div>
  )

  return (
    <DetailsSection title={`${isSuccess ? 'Session' : 'Visit'} summary`}>
      {!!images?.list?.length && (
        <>
          <DetailsValue label="Photos" value={<ImagesContent visit={visit} />} />
          <hr />
        </>
      )}
      {!!assessmentCompleted && (
        <>
          <DetailsValue
            label="Intake Form"
            value={
              <button
                className="btn btn-neutral w-max"
                type="button"
                onClick={() => Modals.open('intakeResults', { visit })}
              >
                Show Intake Form
              </button>
            }
          />
          <hr />
        </>
      )}
      {!!safetyPlan?.form && (
        <>
          <DetailsValue
            label="Safety Plan"
            value={
              <button
                className="btn btn-neutral w-max"
                type="button"
                onClick={() => Modals.open('safetyPlan', { visit })}
              >
                View Safety Plan
              </button>
            }
          />
          <hr />
        </>
      )}
      <DetailsValue label={`${isSuccess ? 'Success' : 'Care'} plan`} value={followupInstructions} />
      <DetailsValue label={isSuccess ? 'Guides' : 'Discharge instructions'} value={displayDischargeInstructions} />
      <DetailsValue label="Restrictions" value={displayRestrictions} />
      <DetailsValue label="History Notes" value={formatNoteDescriptionFromBackend(notes?.history?.description)} />
      <DetailsValue label="Exam Notes" value={formatNoteDescriptionFromBackend(notes?.exam?.description)} />
      {!!chat?.length && (
        <>
          <DetailsValue
            label="Chat"
            value={
              <button
                type="button"
                className="btn btn-neutral w-max"
                onClick={() => Modals.open('chatHistory', { visit })}
              >
                Chat History
              </button>
            }
          />
          <hr />
        </>
      )}
      {!!canEditPsychotherapyNotes && (
        <>
          <DetailsValue
            label={privateNotesLabel}
            value={
              <>
                <DefaultNotesBlock type="psychotherapy" visit={visit} />
                <button
                  className="btn btn-neutral"
                  type="button"
                  onClick={() => Modals.open('psychotherapyNotes', { visit })}
                  disabled={isMemberLocked}
                >
                  {notes?.psychotherapy ? 'Edit' : 'Add'} {privateNotesLabel}
                </button>
              </>
            }
          />
          <hr />
        </>
      )}
      <DetailsValue
        label="Addendum Notes"
        value={
          <>
            <DefaultPluralNotesBlock type="addendum" visit={visit} />
            <button
              className="btn btn-neutral w-max"
              type="button"
              onClick={() => Modals.open('addAddendum', { visit, parentVisit })}
              disabled={isMemberLocked}
            >
              Add Addendum
            </button>
          </>
        }
      />
    </DetailsSection>
  )
}

export default observer(SummarySection)
